import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { updateOrgSettings, restoreOrgDefaults, getInvoiceSettings, updateInvoiceSettings, getBillingEntities, sendTestEmail, getCreditNoteSettings, updateCreditNoteSettings } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useOrgSettings, useFirmName, ORG_DEFAULTS, OrgSettings as OrgSettingsType } from '../contexts/OrgSettingsContext';
import { formatDateDdMmmYy } from '../utils/format';

// ─── Types (mirror the server DEFAULTS shape) ────────────────────────────────

type GeneralSettings        = typeof ORG_DEFAULTS.general;
type AttendanceSettings     = typeof ORG_DEFAULTS.attendance;
type LeaveSettings          = typeof ORG_DEFAULTS.leave;
type TaskSettings           = typeof ORG_DEFAULTS.tasks;
type InvoicingSettings      = typeof ORG_DEFAULTS.invoicing;
type TimesheetSettings      = typeof ORG_DEFAULTS.timesheets;
type SecuritySettings       = typeof ORG_DEFAULTS.security;
type HRSettings             = typeof ORG_DEFAULTS.hr;
type ReimbursementSettings  = typeof ORG_DEFAULTS.reimbursement;
type TicketsSettings        = typeof ORG_DEFAULTS.tickets;
type NotificationsSettings  = typeof ORG_DEFAULTS.notifications;
type KpiSettings            = typeof ORG_DEFAULTS.kpi;
type RolesSettings          = typeof ORG_DEFAULTS.roles;
type StorageSettings        = typeof ORG_DEFAULTS.storage;
type GstSettings            = typeof ORG_DEFAULTS.gst;
type ReceivablesSettings    = typeof ORG_DEFAULTS.receivables;
type ReceiptsSettings       = typeof ORG_DEFAULTS.receipts;
type ReconciliationSettings = typeof ORG_DEFAULTS.reconciliation;
type DocumentsSettings      = typeof ORG_DEFAULTS.documents;
type EmailSettings          = typeof ORG_DEFAULTS.email;
interface BillingSettings {
  senderEmail: string;
  senderName: string;
  defaultCc: string;
  subjectTemplate: string;
  bodyTemplate: string;
}

// ─── Shared UI primitives ────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div className="relative">
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <div className={`w-10 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300'}`} />
        <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="sm:w-64 shrink-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-1">{children}</h3>;
}

function NumberInput({ value, onChange, min, max, step = 1, className = '' }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; className?: string;
}) {
  return (
    <input type="number" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={`input-field w-32 ${className}`} />
  );
}

/**
 * Shows "↺ Default: X" next to a field once it's been changed from the
 * factory value, and resets it back on click. Keeps "what did I change from
 * stock" visible without having to compare against a separate reference.
 */
function DefaultChip<T>({ value, def, onReset }: { value: T; def: T; onReset: () => void }) {
  const isDefault = JSON.stringify(value) === JSON.stringify(def);
  if (isDefault) return null;
  const label = Array.isArray(def) ? def.join(', ') : typeof def === 'boolean' ? (def ? 'On' : 'Off') : String(def);
  return (
    <button type="button" onClick={onReset}
      className="ml-2 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap align-middle"
      title="Reset this field to its factory default">
      ↺ Default: {label}
    </button>
  );
}

// ─── Tab Panels ──────────────────────────────────────────────────────────────

const GeneralTab: React.FC<{ data: GeneralSettings; onChange: (d: GeneralSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<GeneralSettings>) => onChange({ ...data, ...patch });
  const toggleDay = (day: number) => {
    const days = data.workingDays.includes(day)
      ? data.workingDays.filter((d) => d !== day)
      : [...data.workingDays, day].sort();
    set({ workingDays: days });
  };
  return (
    <div>
      <SectionTitle>Organisation</SectionTitle>
      <FieldRow label="Organisation Name" hint="Shown in reports and documents">
        <input className="input-field max-w-xs" value={data.orgName} onChange={(e) => set({ orgName: e.target.value })} />
      </FieldRow>
      <FieldRow label="Currency" hint="Default currency for invoices and amounts">
        <select className="input-field max-w-xs" value={data.currency} onChange={(e) => set({ currency: e.target.value })}>
          <option value="INR">INR — Indian Rupee (₹)</option>
          <option value="USD">USD — US Dollar ($)</option>
          <option value="EUR">EUR — Euro (€)</option>
          <option value="GBP">GBP — British Pound (£)</option>
        </select>
      </FieldRow>
      <FieldRow label="Date Format" hint="How dates are displayed across the app">
        <select className="input-field max-w-xs" value={data.dateFormat} onChange={(e) => set({ dateFormat: e.target.value })}>
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
        </select>
      </FieldRow>
      <FieldRow label="Time Zone" hint="Server-side date calculations use this timezone">
        <select className="input-field max-w-xs" value={data.timeZone} onChange={(e) => set({ timeZone: e.target.value })}>
          <option value="Asia/Kolkata">Asia/Kolkata (IST +5:30)</option>
          <option value="UTC">UTC</option>
          <option value="America/New_York">America/New_York (EST)</option>
          <option value="Europe/London">Europe/London (GMT)</option>
        </select>
      </FieldRow>

      <SectionTitle>Financial Year</SectionTitle>
      <FieldRow label="Fiscal Year Start Month" hint="Month in which the financial year begins (Indian default: April)">
        <select className="input-field max-w-xs" value={data.fiscalYearStartMonth}
          onChange={(e) => set({ fiscalYearStartMonth: Number(e.target.value) })}>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </FieldRow>

      <SectionTitle>Working Days</SectionTitle>
      <FieldRow label="Working Days" hint="Days staff are expected to work (Mon-Sat by default — Sunday only off)">
        <div className="flex gap-2 flex-wrap">
          {WEEKDAYS.map((day, i) => (
            <button key={i} type="button" onClick={() => toggleDay(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                data.workingDays.includes(i) ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
              {day}
            </button>
          ))}
        </div>
      </FieldRow>
    </div>
  );
};

const AttendanceTab: React.FC<{ data: AttendanceSettings; onChange: (d: AttendanceSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<AttendanceSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Check-in Proof Requirements</SectionTitle>
      <FieldRow label="Require Selfie Photo" hint="Staff must take a selfie when marking attendance">
        <Toggle checked={data.photoRequired} onChange={(v) => set({ photoRequired: v })} label={data.photoRequired ? 'Required' : 'Not required'} />
      </FieldRow>
      <FieldRow label="Require GPS Location" hint="Staff GPS coordinates captured at check-in">
        <Toggle checked={data.locationRequired} onChange={(v) => set({ locationRequired: v })} label={data.locationRequired ? 'Required' : 'Not required'} />
      </FieldRow>
      <FieldRow label="Admin / Partner Bypass" hint="ADMIN, IT and Partners can mark attendance without photo/location">
        <Toggle checked={data.adminBypassProof} onChange={(v) => set({ adminBypassProof: v })} label={data.adminBypassProof ? 'Bypass enabled' : 'All roles required'} />
      </FieldRow>
      <FieldRow label="HR Bypass" hint="HR users also bypass photo/location requirements">
        <Toggle checked={data.hrBypassProof} onChange={(v) => set({ hrBypassProof: v })} label={data.hrBypassProof ? 'Bypass enabled' : 'HR must provide proof'} />
      </FieldRow>

      <SectionTitle>Rules</SectionTitle>
      <FieldRow label="Block Backdated Self-Mark" hint="Prevent non-exempt staff from marking attendance for past dates">
        <Toggle checked={data.blockBackdatedSelfMark} onChange={(v) => set({ blockBackdatedSelfMark: v })} label={data.blockBackdatedSelfMark ? 'Blocked' : 'Allowed'} />
      </FieldRow>
      <FieldRow label="Block Re-mark Same Day" hint="Once checked-in, staff cannot mark themselves again on the same day">
        <Toggle checked={data.blockRemarkSameDay} onChange={(v) => set({ blockRemarkSameDay: v })} label={data.blockRemarkSameDay ? 'Blocked' : 'Allowed'} />
      </FieldRow>

      <SectionTitle>Automation</SectionTitle>
      <FieldRow label="Auto-mark Absent" hint="Cron auto-fills 'Absent' for staff who didn't check-in on working days">
        <Toggle checked={data.autoFillAbsent} onChange={(v) => set({ autoFillAbsent: v })} label={data.autoFillAbsent ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Purge Old Records After" hint="Old attendance records are deleted after this many days (housekeeping)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.purgeOldRecordsDays} onChange={(v) => set({ purgeOldRecordsDays: v })} min={7} max={3650} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
    </div>
  );
};

const LeaveTab: React.FC<{ data: LeaveSettings; onChange: (d: LeaveSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<LeaveSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Leave Accrual</SectionTitle>
      <FieldRow label="Accrual Per Month" hint="Leave days credited at the start of each employed month (resets every calendar year); opening balances and carry-forward are set per staff in Payroll">
        <div className="flex items-center gap-2">
          <NumberInput value={data.accrualPerMonth} onChange={(v) => set({ accrualPerMonth: v })} min={0} max={31} step={0.5} />
          <span className="text-sm text-gray-500">days / month</span>
        </div>
      </FieldRow>

      <SectionTitle>Leave Rules</SectionTitle>
      <FieldRow label="Comp-off Enabled" hint="Allow staff to claim compensatory off for extra working days">
        <Toggle checked={data.compOffEnabled} onChange={(v) => set({ compOffEnabled: v })} label={data.compOffEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Leave Approval Required" hint="Leaves must be approved by HR / Admin / Reporting Partner before being granted">
        <Toggle checked={data.leaveApprovalRequired} onChange={(v) => set({ leaveApprovalRequired: v })} label={data.leaveApprovalRequired ? 'Approval required' : 'Auto-approved'} />
      </FieldRow>
      <FieldRow label="Overlap Check" hint="Block creating a leave that overlaps an existing APPROVED or PENDING leave">
        <Toggle checked={data.overlapCheckEnabled} onChange={(v) => set({ overlapCheckEnabled: v })} label={data.overlapCheckEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
    </div>
  );
};

const TasksTab: React.FC<{ data: TaskSettings; onChange: (d: TaskSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<TaskSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Task Numbering</SectionTitle>
      <FieldRow label="Task ID Prefix" hint="Prefix applied to task IDs (current default: T → T0001, T0002, …)">
        <input className="input-field max-w-xs" value={data.taskIdPrefix}
          onChange={(e) => set({ taskIdPrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') })}
          maxLength={10} placeholder="T" />
      </FieldRow>
      <FieldRow label="ID Pad Length" hint="Number of digits to zero-pad the sequence (e.g. 4 → 0001)">
        <NumberInput value={data.taskIdPadLength} onChange={(v) => set({ taskIdPadLength: v })} min={3} max={8} />
      </FieldRow>

      <SectionTitle>Defaults</SectionTitle>
      <FieldRow label="Default Status" hint="Status assigned to newly created tasks">
        <select className="input-field max-w-xs" value={data.defaultStatus}
          onChange={(e) => set({ defaultStatus: e.target.value as 'OPEN' | 'CLOSED' })}>
          <option value="OPEN">OPEN</option>
          <option value="CLOSED">CLOSED</option>
        </select>
      </FieldRow>
      <FieldRow label="Default Billing Status" hint="Billing status for new tasks">
        <select className="input-field max-w-xs" value={data.defaultBillingStatus}
          onChange={(e) => set({ defaultBillingStatus: e.target.value as 'BILLED' | 'UNBILLED' })}>
          <option value="UNBILLED">UNBILLED</option>
          <option value="BILLED">BILLED</option>
        </select>
      </FieldRow>

      <SectionTitle>Closing Tasks</SectionTitle>
      <FieldRow label="Require Archive on Close" hint="Tasks must have an archiveLink + archivingConfirmed=true before they can be closed">
        <Toggle checked={data.requireArchiveOnClose} onChange={(v) => set({ requireArchiveOnClose: v })} label={data.requireArchiveOnClose ? 'Required' : 'Not required'} />
      </FieldRow>
      <FieldRow label="Skip-archive Category" hint="Tasks in this category can be closed without an archive link">
        <input className="input-field max-w-xs" value={data.caCategoryName}
          onChange={(e) => set({ caCategoryName: e.target.value })} placeholder="CA Certification" />
      </FieldRow>
    </div>
  );
};

// ── Invoice Series section (self-contained, own API calls) ───────────────────

interface BillingEntityLite { id: number; name: string; }
interface InvoiceSeriesRow { id: number; billingEntityId: number | null; prefix: string; suffix: string; startNumber: number; }

const InvoiceSeriesSection: React.FC = () => {
  const [bes, setBes] = useState<BillingEntityLite[]>([]);
  const [rows, setRows] = useState<InvoiceSeriesRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Record<string, string>>({});

  useEffect(() => {
    Promise.all([getBillingEntities(), getInvoiceSettings()])
      .then(([beRes, isRes]) => { setBes(beRes.data); setRows(isRes.data); })
      .catch(() => {});
  }, []);

  const getRow = (beId: number | null): InvoiceSeriesRow =>
    rows.find(r => beId === null ? r.billingEntityId == null : r.billingEntityId === beId) ??
    { id: 0, billingEntityId: beId, prefix: '', suffix: '', startNumber: 1 };

  const setField = (beId: number | null, field: keyof InvoiceSeriesRow, val: any) => {
    setRows(prev => {
      const exists = prev.find(r => beId === null ? r.billingEntityId == null : r.billingEntityId === beId);
      if (exists) return prev.map(r => (beId === null ? r.billingEntityId == null : r.billingEntityId === beId) ? { ...r, [field]: val } : r);
      return [...prev, { id: 0, billingEntityId: beId, prefix: '', suffix: '', startNumber: 1, [field]: val }];
    });
  };

  const handleSave = async (beId: number | null) => {
    const key = String(beId ?? 'global');
    setSaving(key); setMsgs(m => ({ ...m, [key]: '' }));
    const row = getRow(beId);
    try {
      const res = await updateInvoiceSettings({ billingEntityId: beId, prefix: row.prefix, suffix: row.suffix, startNumber: row.startNumber });
      const updated: InvoiceSeriesRow = res.data;
      setRows(prev => prev.find(r => r.id === updated.id) ? prev.map(r => r.id === updated.id ? updated : r) : [...prev, updated]);
      setMsgs(m => ({ ...m, [key]: 'Saved.' }));
    } catch { setMsgs(m => ({ ...m, [key]: 'Failed.' })); }
    finally { setSaving(null); }
  };

  const allEntities: Array<{ id: number | null; name: string }> = [
    { id: null, name: 'Global Default (entities without specific settings)' },
    ...bes.map(be => ({ id: be.id, name: be.name })),
  ];

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Each billing entity can have its own prefix, suffix and sequence. The Global Default applies when no entity-specific setting exists.</p>
      {allEntities.map(({ id: beId, name }) => {
        const row = getRow(beId);
        const key = String(beId ?? 'global');
        const preview = `${row.prefix}${row.startNumber}${row.suffix}`;
        return (
          <div key={key} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-800">{name}</span>
              <span className="text-xs text-gray-400 bg-gray-50 border rounded px-2 py-0.5">Next: <strong>{preview}</strong></span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Prefix</label>
                <input className="input-field" value={row.prefix} onChange={e => setField(beId, 'prefix', e.target.value)} placeholder="e.g. INV/" />
              </div>
              <div>
                <label className="label">Start / Current #</label>
                <input type="number" className="input-field" min={1} value={row.startNumber} onChange={e => setField(beId, 'startNumber', parseInt(e.target.value) || 1)} />
              </div>
              <div>
                <label className="label">Suffix</label>
                <input className="input-field" value={row.suffix} onChange={e => setField(beId, 'suffix', e.target.value)} placeholder="e.g. /26-27" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              {msgs[key] ? <span className={`text-xs ${msgs[key] === 'Saved.' ? 'text-green-600' : 'text-red-600'}`}>{msgs[key]}</span> : <span />}
              <button onClick={() => handleSave(beId)} disabled={saving === key} className="btn-primary text-sm py-1 px-4 disabled:opacity-50">
                {saving === key ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Credit Note Series section (per billing entity + global default) ─────────

interface CreditNoteSeriesRow { id: number; billingEntityId: number | null; prefix: string; suffix: string; startNumber: number; }

const CreditNoteSeriesSection: React.FC = () => {
  const [bes, setBes] = useState<BillingEntityLite[]>([]);
  const [rows, setRows] = useState<CreditNoteSeriesRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Record<string, string>>({});

  useEffect(() => {
    Promise.all([getBillingEntities(), getCreditNoteSettings()])
      .then(([beRes, cnRes]) => { setBes(beRes.data); setRows(cnRes.data); })
      .catch(() => {});
  }, []);

  const getRow = (beId: number | null): CreditNoteSeriesRow =>
    rows.find(r => beId === null ? r.billingEntityId == null : r.billingEntityId === beId) ??
    { id: 0, billingEntityId: beId, prefix: 'CN-', suffix: '', startNumber: 1 };

  const setField = (beId: number | null, field: keyof CreditNoteSeriesRow, val: any) => {
    setRows(prev => {
      const exists = prev.find(r => beId === null ? r.billingEntityId == null : r.billingEntityId === beId);
      if (exists) return prev.map(r => (beId === null ? r.billingEntityId == null : r.billingEntityId === beId) ? { ...r, [field]: val } : r);
      return [...prev, { id: 0, billingEntityId: beId, prefix: 'CN-', suffix: '', startNumber: 1, [field]: val }];
    });
  };

  const handleSave = async (beId: number | null) => {
    const key = String(beId ?? 'global');
    setSaving(key); setMsgs(m => ({ ...m, [key]: '' }));
    const row = getRow(beId);
    try {
      const res = await updateCreditNoteSettings({ billingEntityId: beId, prefix: row.prefix, suffix: row.suffix, startNumber: row.startNumber });
      const updated: CreditNoteSeriesRow = res.data;
      setRows(prev => prev.find(r => r.id === updated.id) ? prev.map(r => r.id === updated.id ? updated : r) : [...prev, updated]);
      setMsgs(m => ({ ...m, [key]: 'Saved.' }));
    } catch { setMsgs(m => ({ ...m, [key]: 'Failed.' })); }
    finally { setSaving(null); }
  };

  const allEntities: Array<{ id: number | null; name: string }> = [
    { id: null, name: 'Global Default (entities without specific settings)' },
    ...bes.map(be => ({ id: be.id, name: be.name })),
  ];

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Each billing entity can have its own credit-note prefix, suffix and sequence. The Global Default applies when no entity-specific setting exists.</p>
      {allEntities.map(({ id: beId, name }) => {
        const row = getRow(beId);
        const key = String(beId ?? 'global');
        const preview = `${row.prefix}${row.startNumber}${row.suffix}`;
        return (
          <div key={key} className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-800">{name}</span>
              <span className="text-xs text-gray-400 bg-gray-50 border rounded px-2 py-0.5">Next: <strong>{preview}</strong></span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Prefix</label>
                <input className="input-field" value={row.prefix} onChange={e => setField(beId, 'prefix', e.target.value)} placeholder="e.g. CN-" />
              </div>
              <div>
                <label className="label">Start / Current #</label>
                <input type="number" className="input-field" min={1} value={row.startNumber} onChange={e => setField(beId, 'startNumber', parseInt(e.target.value) || 1)} />
              </div>
              <div>
                <label className="label">Suffix</label>
                <input className="input-field" value={row.suffix} onChange={e => setField(beId, 'suffix', e.target.value)} placeholder="e.g. /26-27" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              {msgs[key] ? <span className={`text-xs ${msgs[key] === 'Saved.' ? 'text-green-600' : 'text-red-600'}`}>{msgs[key]}</span> : <span />}
              <button onClick={() => handleSave(beId)} disabled={saving === key} className="btn-primary text-sm py-1 px-4 disabled:opacity-50">
                {saving === key ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InvoicingTab: React.FC<{ data: InvoicingSettings; onChange: (d: InvoicingSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<InvoicingSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Default Tax Settings</SectionTitle>
      <FieldRow label="Default Tax Type" hint="Pre-selected tax type when creating a new invoice (current default: NONE)">
        <select className="input-field max-w-xs" value={data.defaultTaxType}
          onChange={(e) => set({ defaultTaxType: e.target.value })}>
          <option value="NONE">No Tax</option>
          <option value="CGST_SGST">CGST + SGST (Intra-state)</option>
          <option value="IGST">IGST (Inter-state)</option>
        </select>
      </FieldRow>
      <FieldRow label="CGST Rate" hint="Default CGST percentage when CGST+SGST is chosen">
        <div className="flex items-center gap-2">
          <NumberInput value={data.defaultCgstRate} onChange={(v) => set({ defaultCgstRate: v })} min={0} max={50} step={0.5} />
          <span className="text-sm text-gray-500">%</span>
        </div>
      </FieldRow>
      <FieldRow label="SGST Rate" hint="Default SGST percentage when CGST+SGST is chosen">
        <div className="flex items-center gap-2">
          <NumberInput value={data.defaultSgstRate} onChange={(v) => set({ defaultSgstRate: v })} min={0} max={50} step={0.5} />
          <span className="text-sm text-gray-500">%</span>
        </div>
      </FieldRow>
      <FieldRow label="IGST Rate" hint="Default IGST percentage when IGST is chosen">
        <div className="flex items-center gap-2">
          <NumberInput value={data.defaultIgstRate} onChange={(v) => set({ defaultIgstRate: v })} min={0} max={50} step={0.5} />
          <span className="text-sm text-gray-500">%</span>
        </div>
      </FieldRow>

      <SectionTitle>HSN / SAC</SectionTitle>
      <FieldRow label="Show HSN/SAC Field" hint="Display the HSN/SAC code column on invoice line items">
        <Toggle checked={data.showHsnSac} onChange={(v) => set({ showHsnSac: v })} label={data.showHsnSac ? 'Shown' : 'Hidden'} />
      </FieldRow>
      <FieldRow label="Require HSN/SAC" hint="Make HSN/SAC code mandatory when creating invoices">
        <Toggle checked={data.requireHsnSac} onChange={(v) => set({ requireHsnSac: v })} label={data.requireHsnSac ? 'Required' : 'Optional'} />
      </FieldRow>

      <SectionTitle>TDS</SectionTitle>
      <FieldRow label="Default TDS Section" hint="Pre-populated TDS section (e.g. 194J). Leave blank for no default.">
        <input className="input-field max-w-xs" value={data.defaultTdsSection}
          onChange={(e) => set({ defaultTdsSection: e.target.value })} placeholder="(no default)" />
      </FieldRow>

      <SectionTitle>Payment</SectionTitle>
      <FieldRow label="Default Payment Due Days" hint="Days from invoice date to due date">
        <div className="flex items-center gap-2">
          <NumberInput value={data.defaultDueDays} onChange={(v) => set({ defaultDueDays: v })} min={0} max={365} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
      <FieldRow label="Default Payment Mode" hint="Pre-selected payment mode when recording a payment">
        <select className="input-field max-w-xs" value={data.defaultPaymentMode}
          onChange={(e) => set({ defaultPaymentMode: e.target.value })}>
          <option value="CASH">Cash</option>
          <option value="BANK">Bank Transfer</option>
          <option value="CHEQUE">Cheque</option>
          <option value="UPI">UPI</option>
          <option value="OTHER">Other</option>
        </select>
      </FieldRow>
      <FieldRow label="Credit Note Prefix" hint="Prefix for credit note numbers">
        <input className="input-field max-w-xs" value={data.creditNotePrefix}
          onChange={(e) => set({ creditNotePrefix: e.target.value.toUpperCase() })} maxLength={10} placeholder="CN-" />
      </FieldRow>

      <SectionTitle>Edit Window</SectionTitle>
      <FieldRow
        label="Edit Window (days)"
        hint="How long after creation a task partner / manager / creator can edit a non-DRAFT invoice. Admin and HR are always exempt. 0 = no edits allowed once created (only Admin/HR)."
      >
        <NumberInput
          value={(data as any).invoiceEditWindowDays ?? 7}
          onChange={(v) => set({ invoiceEditWindowDays: v } as any)}
          min={0}
          max={365}
        />
      </FieldRow>
      <FieldRow
        label="Draft invoices always editable"
        hint="When ON, DRAFT invoices can be edited by anyone with permission regardless of the window above. Recommended ON — drafts aren't sent to clients yet."
      >
        <Toggle
          checked={(data as any).draftInvoicesAlwaysEditable !== false}
          onChange={(v) => set({ draftInvoicesAlwaysEditable: v } as any)}
          label={(data as any).draftInvoicesAlwaysEditable !== false ? 'On' : 'Off'}
        />
      </FieldRow>

      <SectionTitle>Retainer Invoice Wizard</SectionTitle>
      <FieldRow
        label="Retainer Close Buffer"
        hint="Quarterly/half-yearly/yearly retainers only show as pending inside this many days of the period closing (or after it's closed) — billing a quarter the day it opens is premature."
      >
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.retainerCloseBufferDays ?? 5} onChange={(v) => set({ retainerCloseBufferDays: v } as any)} min={0} max={30} />
          <span className="text-sm text-gray-500">days</span>
          <DefaultChip value={data.retainerCloseBufferDays ?? 5} def={ORG_DEFAULTS.invoicing.retainerCloseBufferDays}
            onReset={() => set({ retainerCloseBufferDays: ORG_DEFAULTS.invoicing.retainerCloseBufferDays } as any)} />
        </div>
      </FieldRow>
      <FieldRow
        label="Bill Monthly Retainer From"
        hint="A monthly retainer for the month in progress only shows as pending from this day of the month onward — billing September on the 1st bills work nobody has done yet. Months already past always show."
      >
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={(data as any).retainerMonthlyBillFromDay ?? 25}
            onChange={(v) => set({ retainerMonthlyBillFromDay: v } as any)} min={1} max={28} />
          <span className="text-sm text-gray-500">of the month</span>
          <DefaultChip value={(data as any).retainerMonthlyBillFromDay ?? 25} def={ORG_DEFAULTS.invoicing.retainerMonthlyBillFromDay}
            onReset={() => set({ retainerMonthlyBillFromDay: ORG_DEFAULTS.invoicing.retainerMonthlyBillFromDay } as any)} />
        </div>
      </FieldRow>
      <FieldRow label="OPE Ticked By Default" hint="Whether the 'include out-of-pocket expenses' checkbox starts ticked for each row in the wizard">
        <div className="flex items-center flex-wrap">
          <Toggle checked={(data as any).wizardOpeDefaultOn !== false} onChange={(v) => set({ wizardOpeDefaultOn: v } as any)}
            label={(data as any).wizardOpeDefaultOn !== false ? 'On' : 'Off'} />
          <DefaultChip value={(data as any).wizardOpeDefaultOn !== false} def={ORG_DEFAULTS.invoicing.wizardOpeDefaultOn}
            onReset={() => set({ wizardOpeDefaultOn: ORG_DEFAULTS.invoicing.wizardOpeDefaultOn } as any)} />
        </div>
      </FieldRow>
      <FieldRow label="Default Invoice Template" hint="Template number the wizard and quick-create default to">
        <div className="flex items-center gap-2 flex-wrap">
          <select className="input-field max-w-xs" value={(data as any).defaultInvoiceTemplate ?? 1}
            onChange={(e) => set({ defaultInvoiceTemplate: Number(e.target.value) } as any)}>
            <option value={1}>Template 1</option>
            <option value={2}>Template 2</option>
            <option value={3}>Template 3</option>
          </select>
          <DefaultChip value={(data as any).defaultInvoiceTemplate ?? 1} def={ORG_DEFAULTS.invoicing.defaultInvoiceTemplate}
            onReset={() => set({ defaultInvoiceTemplate: ORG_DEFAULTS.invoicing.defaultInvoiceTemplate } as any)} />
        </div>
      </FieldRow>
      <FieldRow
        label="Default Description Template"
        hint="Used for a retainer period's line-item description when the schedule has none set. {taskName} and {periodLabel} are substituted."
      >
        <div className="flex items-center gap-2 flex-wrap">
          <input className="input-field max-w-sm" value={(data as any).wizardDefaultDescriptionTemplate ?? ORG_DEFAULTS.invoicing.wizardDefaultDescriptionTemplate}
            onChange={(e) => set({ wizardDefaultDescriptionTemplate: e.target.value } as any)} maxLength={200} />
          <DefaultChip value={(data as any).wizardDefaultDescriptionTemplate ?? ORG_DEFAULTS.invoicing.wizardDefaultDescriptionTemplate} def={ORG_DEFAULTS.invoicing.wizardDefaultDescriptionTemplate}
            onReset={() => set({ wizardDefaultDescriptionTemplate: ORG_DEFAULTS.invoicing.wizardDefaultDescriptionTemplate } as any)} />
        </div>
      </FieldRow>

      <SectionTitle>Invoice Series / Numbering</SectionTitle>
      <div className="py-2">
        <InvoiceSeriesSection />
      </div>

      <SectionTitle>Credit Note Series / Numbering</SectionTitle>
      <div className="py-2">
        <CreditNoteSeriesSection />
      </div>
    </div>
  );
};

const TimesheetsTab: React.FC<{ data: TimesheetSettings; onChange: (d: TimesheetSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<TimesheetSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Week Configuration</SectionTitle>
      <FieldRow label="Week Starts On" hint="First day of the week for timesheet entries">
        <select className="input-field max-w-xs" value={data.weekStartsOn}
          onChange={(e) => set({ weekStartsOn: Number(e.target.value) })}>
          {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
        </select>
      </FieldRow>
      <FieldRow label="Hours Step" hint="Smallest increment of hours allowed in timesheet entries (1 = whole hours only)">
        <NumberInput value={data.hoursStep} onChange={(v) => set({ hoursStep: v })} min={0.25} max={12} step={0.25} />
      </FieldRow>

      <SectionTitle>Locking</SectionTitle>
      <FieldRow label="Weekly Lock Enabled" hint="HR/Admin can lock past weeks to prevent further edits">
        <Toggle checked={data.lockEnabled} onChange={(v) => set({ lockEnabled: v })} label={data.lockEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>

      <SectionTitle>Entry Rules</SectionTitle>
      <FieldRow label="Allow Backdated Entry" hint="Allow staff to log hours for past dates (subject to weekly lock)">
        <Toggle checked={data.allowBackdatedEntry} onChange={(v) => set({ allowBackdatedEntry: v })} label={data.allowBackdatedEntry ? 'Allowed' : 'Not allowed'} />
      </FieldRow>
      <FieldRow label="Block on Approved Leave" hint="Cannot log hours on a date when staff is on approved leave">
        <Toggle checked={data.blockOnApprovedLeave} onChange={(v) => set({ blockOnApprovedLeave: v })} label={data.blockOnApprovedLeave ? 'Blocked' : 'Allowed'} />
      </FieldRow>
      <FieldRow label="Block on Closed Task" hint="Cannot log hours against tasks that are CLOSED or frozen">
        <Toggle checked={data.blockOnClosedTask} onChange={(v) => set({ blockOnClosedTask: v })} label={data.blockOnClosedTask ? 'Blocked' : 'Allowed'} />
      </FieldRow>
    </div>
  );
};

const SecurityTab: React.FC<{ data: SecuritySettings; onChange: (d: SecuritySettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<SecuritySettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Session</SectionTitle>
      <FieldRow label="Session Timeout" hint="How long a JWT token lasts before the user must log in again (current: 8 hours)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.sessionTimeoutHours} onChange={(v) => set({ sessionTimeoutHours: v })} min={1} max={168} />
          <span className="text-sm text-gray-500">hours</span>
        </div>
      </FieldRow>

      <SectionTitle>Password Policy</SectionTitle>
      <FieldRow label="Minimum Password Length" hint="Passwords shorter than this are rejected">
        <div className="flex items-center gap-2">
          <NumberInput value={data.minPasswordLength} onChange={(v) => set({ minPasswordLength: v })} min={4} max={32} />
          <span className="text-sm text-gray-500">characters</span>
        </div>
      </FieldRow>
      <FieldRow label="Require Strong Password" hint="Require upper-case, lower-case, digit, and special character">
        <Toggle checked={data.requireStrongPassword} onChange={(v) => set({ requireStrongPassword: v })} label={data.requireStrongPassword ? 'Required' : 'Not required'} />
      </FieldRow>
      <FieldRow label="Bcrypt Rounds" hint="Higher = more secure but slower (10 is the standard)">
        <NumberInput value={data.bcryptRounds} onChange={(v) => set({ bcryptRounds: v })} min={8} max={14} />
      </FieldRow>

      <SectionTitle>Sign-in Options</SectionTitle>
      <FieldRow
        label="Sign in with a Code"
        hint="Let staff sign in with a 6-digit code emailed to them instead of a password. The code expires in 10 minutes and can be used once."
      >
        <Toggle
          checked={data.otpLoginEnabled}
          onChange={(v) => set({ otpLoginEnabled: v })}
          label={data.otpLoginEnabled ? 'Allowed' : 'Password only'}
        />
      </FieldRow>

      <FieldRow
        label="Face ID & Fingerprint"
        hint="Let staff sign in with their device's own fingerprint reader or face unlock. No biometric data reaches the server — the device keeps it and only signs a challenge. Staff enrol each device from their profile."
      >
        <Toggle
          checked={data.passkeyLoginEnabled}
          onChange={(v) => set({ passkeyLoginEnabled: v })}
          label={data.passkeyLoginEnabled ? 'Allowed' : 'Not allowed'}
        />
      </FieldRow>
    </div>
  );
};

const HRTab: React.FC<{ data: HRSettings; onChange: (d: HRSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<HRSettings>) => onChange({ ...data, ...patch });
  const toggleWeeklyOff = (day: number) => {
    const days = data.weeklyOffDays.includes(day)
      ? data.weeklyOffDays.filter((d) => d !== day)
      : [...data.weeklyOffDays, day].sort();
    set({ weeklyOffDays: days });
  };
  return (
    <div>
      <SectionTitle>Work Schedule</SectionTitle>
      <FieldRow label="Working Hours Per Day" hint="Standard daily working hours (used for capacity calculations — current: 8)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.workingHoursPerDay} onChange={(v) => set({ workingHoursPerDay: v })} min={1} max={24} />
          <span className="text-sm text-gray-500">hours / day</span>
        </div>
      </FieldRow>
      <FieldRow label="Weekly Off Days" hint="Days the office is closed (current: Sunday only)">
        <div className="flex gap-2 flex-wrap">
          {WEEKDAYS.map((day, i) => (
            <button key={i} type="button" onClick={() => toggleWeeklyOff(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                data.weeklyOffDays.includes(i) ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-red-400'}`}>
              {day}
            </button>
          ))}
        </div>
      </FieldRow>
    </div>
  );
};

const ReimbursementTab: React.FC<{ data: ReimbursementSettings; onChange: (d: ReimbursementSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<ReimbursementSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Limits</SectionTitle>
      <FieldRow label="Require Receipt Above" hint="Items above this amount must have a receipt attached">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">₹</span>
          <NumberInput value={data.requireReceiptAboveAmount} onChange={(v) => set({ requireReceiptAboveAmount: v })} min={0} max={10000000} step={100} />
        </div>
      </FieldRow>
      <FieldRow label="Max Per Submission" hint="Maximum total amount across all items in a single claim">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">₹</span>
          <NumberInput value={data.maxClaimPerSubmission} onChange={(v) => set({ maxClaimPerSubmission: v })} min={0} max={10000000} step={1000} />
        </div>
      </FieldRow>
      <FieldRow label="Auto-approve Below" hint="Claims under this amount skip manual review (0 = disabled)">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">₹</span>
          <NumberInput value={data.autoApproveBelowAmount} onChange={(v) => set({ autoApproveBelowAmount: v })} min={0} max={10000000} step={100} />
        </div>
      </FieldRow>

      <SectionTitle>Notifications</SectionTitle>
      <FieldRow label="Notify Approvers on Submit" hint="Email + SMS partners/admins when a claim is submitted">
        <Toggle checked={data.notifyOnSubmit} onChange={(v) => set({ notifyOnSubmit: v })} label={data.notifyOnSubmit ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Notify Staff on Decision" hint="Email + SMS the staff member when their claim is approved/rejected/returned">
        <Toggle checked={data.notifyOnDecision} onChange={(v) => set({ notifyOnDecision: v })} label={data.notifyOnDecision ? 'Enabled' : 'Disabled'} />
      </FieldRow>
    </div>
  );
};

const TicketsTab: React.FC<{ data: TicketsSettings; onChange: (d: TicketsSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<TicketsSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Defaults</SectionTitle>
      <FieldRow label="Default Priority" hint="Pre-selected priority when a ticket is created">
        <select className="input-field max-w-xs" value={data.defaultPriority}
          onChange={(e) => set({ defaultPriority: e.target.value as TicketsSettings['defaultPriority'] })}>
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </select>
      </FieldRow>
      <FieldRow label="Default Type" hint="Pre-selected ticket type when a ticket is created">
        <select className="input-field max-w-xs" value={data.defaultType}
          onChange={(e) => set({ defaultType: e.target.value as TicketsSettings['defaultType'] })}>
          <option value="SOFTWARE">Software</option>
          <option value="HARDWARE">Hardware</option>
          <option value="NETWORK">Network</option>
          <option value="OTHER">Other</option>
        </select>
      </FieldRow>

      <SectionTitle>SLA Targets</SectionTitle>
      <FieldRow label="Urgent" hint="Target resolution time for Urgent tickets">
        <div className="flex items-center gap-2">
          <NumberInput value={data.slaHoursUrgent} onChange={(v) => set({ slaHoursUrgent: v })} min={1} max={168} />
          <span className="text-sm text-gray-500">hours</span>
        </div>
      </FieldRow>
      <FieldRow label="High" hint="Target resolution time for High priority tickets">
        <div className="flex items-center gap-2">
          <NumberInput value={data.slaHoursHigh} onChange={(v) => set({ slaHoursHigh: v })} min={1} max={720} />
          <span className="text-sm text-gray-500">hours</span>
        </div>
      </FieldRow>
      <FieldRow label="Medium" hint="Target resolution time for Medium priority tickets">
        <div className="flex items-center gap-2">
          <NumberInput value={data.slaHoursMedium} onChange={(v) => set({ slaHoursMedium: v })} min={1} max={720} />
          <span className="text-sm text-gray-500">hours</span>
        </div>
      </FieldRow>
      <FieldRow label="Low" hint="Target resolution time for Low priority tickets">
        <div className="flex items-center gap-2">
          <NumberInput value={data.slaHoursLow} onChange={(v) => set({ slaHoursLow: v })} min={1} max={2160} />
          <span className="text-sm text-gray-500">hours</span>
        </div>
      </FieldRow>

      <SectionTitle>Automation</SectionTitle>
      <FieldRow label="Auto-close RESOLVED tickets" hint="Auto-close after N days in RESOLVED status (0 = never)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.autoCloseResolvedAfterDays} onChange={(v) => set({ autoCloseResolvedAfterDays: v })} min={0} max={365} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
      <FieldRow label="Partner Approval Threshold" hint="Tickets with estimated cost above this need partner sign-off">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">₹</span>
          <NumberInput value={data.costApprovalRequiredAbove} onChange={(v) => set({ costApprovalRequiredAbove: v })} min={0} max={10000000} step={100} />
        </div>
      </FieldRow>
    </div>
  );
};

const NotificationsTab: React.FC<{ data: NotificationsSettings; onChange: (d: NotificationsSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<NotificationsSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Task Reminders</SectionTitle>
      <FieldRow label="Reminder Window" hint="Flag tasks due within this many days in the daily digest">
        <div className="flex items-center gap-2">
          <NumberInput value={data.taskDueReminderDays} onChange={(v) => set({ taskDueReminderDays: v })} min={0} max={30} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
      <FieldRow label="Daily Cron Time" hint="When the daily reminder digest runs (IST, HH:MM). Requires server restart to apply.">
        <input type="time" className="input-field max-w-xs" value={data.taskReminderCronTime}
          onChange={(e) => set({ taskReminderCronTime: e.target.value })} />
      </FieldRow>

      <SectionTitle>Invoice Reminders</SectionTitle>
      <FieldRow label="Overdue Reminder After" hint="Days after the due date before the first overdue reminder fires">
        <div className="flex items-center gap-2">
          <NumberInput value={data.invoiceOverdueReminderDays} onChange={(v) => set({ invoiceOverdueReminderDays: v })} min={0} max={365} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>

      <SectionTitle>Channels</SectionTitle>
      <FieldRow label="Email Notifications" hint="Global on/off switch for all transactional emails">
        <Toggle checked={data.emailNotificationsEnabled} onChange={(v) => set({ emailNotificationsEnabled: v })} label={data.emailNotificationsEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="SMS Notifications" hint="Global on/off switch for all SMS alerts">
        <Toggle checked={data.smsNotificationsEnabled} onChange={(v) => set({ smsNotificationsEnabled: v })} label={data.smsNotificationsEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Attendance Reminders" hint="Send morning attendance reminders to staff who haven't checked in">
        <Toggle checked={data.attendanceReminderEnabled} onChange={(v) => set({ attendanceReminderEnabled: v })} label={data.attendanceReminderEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>

      <SectionTitle>Feature Notifications</SectionTitle>
      <FieldRow label="GST Filing Reminders" hint="Notify users when GST returns are due or filing status changes">
        <Toggle checked={data.gstFilingReminderEnabled} onChange={(v) => set({ gstFilingReminderEnabled: v })} label={data.gstFilingReminderEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Reimbursement Paid Notification" hint="Notify staff when their reimbursement is marked as PAID">
        <Toggle checked={data.reimbursementPaidNotification} onChange={(v) => set({ reimbursementPaidNotification: v })} label={data.reimbursementPaidNotification ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Monthly Firm Digest" hint="Email active partners a billed/collected/outstanding summary on the 1st of each month">
        <Toggle checked={data.monthlyDigestEnabled} onChange={(v) => set({ monthlyDigestEnabled: v })} label={data.monthlyDigestEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
    </div>
  );
};

const KpiTab: React.FC<{ data: KpiSettings; onChange: (d: KpiSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<KpiSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Client Health Score</SectionTitle>
      <FieldRow label="🟢 GOOD threshold" hint="Score at or above this value is shown as GOOD">
        <div className="flex items-center gap-2">
          <NumberInput value={data.healthScoreGoodMin} onChange={(v) => set({ healthScoreGoodMin: v })} min={0} max={100} />
          <span className="text-sm text-gray-500">/ 100</span>
        </div>
      </FieldRow>
      <FieldRow label="🟡 AT_RISK threshold" hint="Score at or above this (but below GOOD) is shown as AT_RISK; otherwise CRITICAL">
        <div className="flex items-center gap-2">
          <NumberInput value={data.healthScoreAtRiskMin} onChange={(v) => set({ healthScoreAtRiskMin: v })} min={0} max={100} />
          <span className="text-sm text-gray-500">/ 100</span>
        </div>
      </FieldRow>

      <SectionTitle>Dashboard Targets</SectionTitle>
      <FieldRow label="Billing Realization Target" hint="Dashboard target for billed-vs-total tasks ratio">
        <div className="flex items-center gap-2">
          <NumberInput value={data.billingRealizationTargetPct} onChange={(v) => set({ billingRealizationTargetPct: v })} min={0} max={100} />
          <span className="text-sm text-gray-500">%</span>
        </div>
      </FieldRow>
      <FieldRow label="Collection Rate Target" hint="Dashboard target for collected-vs-invoiced ratio">
        <div className="flex items-center gap-2">
          <NumberInput value={data.collectionRateTargetPct} onChange={(v) => set({ collectionRateTargetPct: v })} min={0} max={100} />
          <span className="text-sm text-gray-500">%</span>
        </div>
      </FieldRow>
      <FieldRow label="Overdue Invoice Threshold" hint="Days past due date before an invoice is flagged as overdue in dashboards">
        <div className="flex items-center gap-2">
          <NumberInput value={data.overdueInvoiceDaysThreshold} onChange={(v) => set({ overdueInvoiceDaysThreshold: v })} min={0} max={365} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
    </div>
  );
};

const RolesTab: React.FC<{ data: RolesSettings; onChange: (d: RolesSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<RolesSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
        ℹ️ These toggles control role-based feature access. Backend enforcement is gradually being wired up — toggles already saved take effect for newly-wired checks.
      </p>

      <SectionTitle>Staff Permissions</SectionTitle>
      <FieldRow label="View All Tasks" hint="Allow regular staff to see tasks they're not assigned to">
        <Toggle checked={data.staffCanViewAllTasks} onChange={(v) => set({ staffCanViewAllTasks: v })} label={data.staffCanViewAllTasks ? 'Allowed' : 'Restricted'} />
      </FieldRow>
      <FieldRow label="Create IT Tickets" hint="Allow staff to raise their own IT tickets">
        <Toggle checked={data.staffCanCreateTickets} onChange={(v) => set({ staffCanCreateTickets: v })} label={data.staffCanCreateTickets ? 'Allowed' : 'Restricted'} />
      </FieldRow>
      <FieldRow label="Create Reimbursement Claims" hint="Allow staff to submit reimbursement claims">
        <Toggle checked={data.staffCanCreateReimbursement} onChange={(v) => set({ staffCanCreateReimbursement: v })} label={data.staffCanCreateReimbursement ? 'Allowed' : 'Restricted'} />
      </FieldRow>
      <FieldRow label="View Others' Timesheets" hint="Allow staff to see colleagues' timesheet entries">
        <Toggle checked={data.staffCanViewOtherTimesheets} onChange={(v) => set({ staffCanViewOtherTimesheets: v })} label={data.staffCanViewOtherTimesheets ? 'Allowed' : 'Restricted'} />
      </FieldRow>

      <FieldRow label="View All Invoices" hint="Off = a staff member sees only the invoices they raised themselves. On = every staff member sees every invoice, firm-wide.">
        <Toggle checked={data.staffCanViewAllInvoices} onChange={(v) => set({ staffCanViewAllInvoices: v })} label={data.staffCanViewAllInvoices ? 'All invoices' : 'Own invoices only'} />
      </FieldRow>
      <FieldRow label="Create Invoices" hint="Allow staff to raise invoices against tasks they manage (otherwise Admin/HR/partner only)">
        <Toggle checked={data.staffCanCreateInvoice} onChange={(v) => set({ staffCanCreateInvoice: v })} label={data.staffCanCreateInvoice ? 'Allowed' : 'Restricted'} />
      </FieldRow>
      <FieldRow label="See Own Reimbursement Paid Status" hint="Show paid date / status to staff on their own claims">
        <Toggle checked={data.staffCanSeeOwnReimbursementPaid} onChange={(v) => set({ staffCanSeeOwnReimbursementPaid: v })} label={data.staffCanSeeOwnReimbursementPaid ? 'Allowed' : 'Hidden'} />
      </FieldRow>
      <FieldRow label="Export Data (Excel)" hint="Allow staff to export tables to Excel">
        <Toggle checked={data.staffCanExportData} onChange={(v) => set({ staffCanExportData: v })} label={data.staffCanExportData ? 'Allowed' : 'Restricted'} />
      </FieldRow>

      <SectionTitle>HR Permissions</SectionTitle>
      <FieldRow label="Reset Staff Passwords" hint="Allow HR to reset other staff members' passwords">
        <Toggle checked={data.hrCanResetPasswords} onChange={(v) => set({ hrCanResetPasswords: v })} label={data.hrCanResetPasswords ? 'Allowed' : 'Admin only'} />
      </FieldRow>
      <FieldRow label="Access Billing" hint="HR sees the whole Invoicing module — Retainers, Reconciliation, Receivables, TDS and payment recording. Switch off to cut HR back to no billing access.">
        <Toggle checked={data.hrCanAccessBilling} onChange={(v) => set({ hrCanAccessBilling: v })} label={data.hrCanAccessBilling ? 'Full billing access' : 'No billing access'} />
      </FieldRow>
      <FieldRow label="Approve Reimbursements" hint="Allow HR to approve/reject reimbursement claims (default: partners only)">
        <Toggle checked={data.hrCanApproveReimbursements} onChange={(v) => set({ hrCanApproveReimbursements: v })} label={data.hrCanApproveReimbursements ? 'Allowed' : 'Partners only'} />
      </FieldRow>
      <FieldRow label="Mark Reimbursements Paid" hint="Allow HR to mark approved reimbursements as PAID">
        <Toggle checked={data.hrCanMarkReimbursementsPaid} onChange={(v) => set({ hrCanMarkReimbursementsPaid: v })} label={data.hrCanMarkReimbursementsPaid ? 'Allowed' : 'Admin only'} />
      </FieldRow>

      <SectionTitle>Partner Permissions</SectionTitle>
      <FieldRow label="Delete Invoices" hint="Allow partners to delete invoices (otherwise admin-only)">
        <Toggle checked={data.partnerCanDeleteInvoices} onChange={(v) => set({ partnerCanDeleteInvoices: v })} label={data.partnerCanDeleteInvoices ? 'Allowed' : 'Admin only'} />
      </FieldRow>
      {/* Cancelling an invoice and marking one unpaid used to be switchable
          here. Both are now part of what being a partner means, so there is
          nothing left to configure. */}
      <FieldRow label="Delete Clients" hint="Allow partners to delete client records (otherwise admin-only)">
        <Toggle checked={data.partnerCanDeleteClients} onChange={(v) => set({ partnerCanDeleteClients: v })} label={data.partnerCanDeleteClients ? 'Allowed' : 'Admin only'} />
      </FieldRow>
      <FieldRow label="Run GST Check" hint="Allow partners to trigger the GST filing status checker">
        <Toggle checked={data.partnerCanRunGstCheck} onChange={(v) => set({ partnerCanRunGstCheck: v })} label={data.partnerCanRunGstCheck ? 'Allowed' : 'Admin only'} />
      </FieldRow>
      <FieldRow label="Import Opening Balance" hint="Allow partners to import opening balances via Excel">
        <Toggle checked={data.partnerCanImportOpeningBalance} onChange={(v) => set({ partnerCanImportOpeningBalance: v })} label={data.partnerCanImportOpeningBalance ? 'Allowed' : 'Admin only'} />
      </FieldRow>
      <FieldRow label="Restrict Opening Balance to Admin/HR" hint="When ON, opening-balance view/manage access drops to Admin/HR only, even for partners">
        <div className="flex items-center flex-wrap">
          <Toggle checked={!!(data as any).restrictOpeningBalanceToAdminHR} onChange={(v) => set({ restrictOpeningBalanceToAdminHR: v } as any)}
            label={(data as any).restrictOpeningBalanceToAdminHR ? 'Restricted' : 'Partners included'} />
          <DefaultChip value={!!(data as any).restrictOpeningBalanceToAdminHR} def={ORG_DEFAULTS.roles.restrictOpeningBalanceToAdminHR}
            onReset={() => set({ restrictOpeningBalanceToAdminHR: ORG_DEFAULTS.roles.restrictOpeningBalanceToAdminHR } as any)} />
        </div>
      </FieldRow>

      <SectionTitle>IT Permissions</SectionTitle>
      <FieldRow label="View All Tickets" hint="Allow IT role to see all tickets across the organisation">
        <Toggle checked={data.itCanViewAllTickets} onChange={(v) => set({ itCanViewAllTickets: v })} label={data.itCanViewAllTickets ? 'Allowed' : 'Assigned only'} />
      </FieldRow>
    </div>
  );
};

const StorageTab: React.FC<{ data: StorageSettings; onChange: (d: StorageSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<StorageSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Upload Limits</SectionTitle>
      <FieldRow label="Maximum File Size" hint="Per-file upload cap across all features">
        <div className="flex items-center gap-2">
          <NumberInput value={data.maxFileSizeMB} onChange={(v) => set({ maxFileSizeMB: v })} min={1} max={200} />
          <span className="text-sm text-gray-500">MB</span>
        </div>
      </FieldRow>

      <SectionTitle>Allowed File Types</SectionTitle>
      <FieldRow label="Images" hint="JPG, PNG, GIF">
        <Toggle checked={data.allowImages} onChange={(v) => set({ allowImages: v })} label={data.allowImages ? 'Allowed' : 'Blocked'} />
      </FieldRow>
      <FieldRow label="PDF" hint="PDF documents">
        <Toggle checked={data.allowPdf} onChange={(v) => set({ allowPdf: v })} label={data.allowPdf ? 'Allowed' : 'Blocked'} />
      </FieldRow>
      <FieldRow label="Office Documents" hint="Word, Excel files (.doc, .docx, .xls, .xlsx)">
        <Toggle checked={data.allowOfficeDocs} onChange={(v) => set({ allowOfficeDocs: v })} label={data.allowOfficeDocs ? 'Allowed' : 'Blocked'} />
      </FieldRow>
      <FieldRow label="ZIP Archives" hint="Compressed archive files">
        <Toggle checked={data.allowZip} onChange={(v) => set({ allowZip: v })} label={data.allowZip ? 'Allowed' : 'Blocked'} />
      </FieldRow>

      <SectionTitle>Retention</SectionTitle>
      <FieldRow label="Client Vault Retention" hint="Auto-delete vault documents after this many days (0 = keep forever)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.vaultRetentionDays} onChange={(v) => set({ vaultRetentionDays: v })} min={0} max={3650} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
      <FieldRow label="Chat Attachment Retention" hint="Auto-delete chat attachments after this many days (0 = keep forever)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.chatRetentionDays} onChange={(v) => set({ chatRetentionDays: v })} min={0} max={3650} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>
      <FieldRow label="Notification Retention" hint="Auto-delete READ notifications after this many days (0 = keep forever). Unread notifications are never purged.">
        <div className="flex items-center gap-2">
          <NumberInput value={data.notificationRetentionDays} onChange={(v) => set({ notificationRetentionDays: v })} min={0} max={3650} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>

      <SectionTitle>Excel Export</SectionTitle>
      <FieldRow label="Excel Export Enabled" hint="Global on/off switch for the universal Excel export feature">
        <Toggle checked={data.excelExportEnabled} onChange={(v) => set({ excelExportEnabled: v })} label={data.excelExportEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Max Export Rows" hint="Hard cap on rows per Excel export (protects memory/perf)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.maxExportRows} onChange={(v) => set({ maxExportRows: v })} min={100} max={1000000} step={1000} />
          <span className="text-sm text-gray-500">rows</span>
        </div>
      </FieldRow>
    </div>
  );
};

const GST_RETURN_OPTIONS = ['GSTR-1', 'GSTR-3B', 'GSTR-6', 'GSTR-4', 'GSTR-9', 'GSTR-2B', 'CMP-08'];

const GstTab: React.FC<{ data: GstSettings; onChange: (d: GstSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<GstSettings> & { whitebooksClientSecret?: string }) =>
    onChange({ ...data, ...patch } as any);
  const [showSecret, setShowSecret] = useState(false);
  const toggleReturn = (rt: string) => {
    const next = data.gstReturnTypes.includes(rt)
      ? data.gstReturnTypes.filter((r) => r !== rt)
      : [...data.gstReturnTypes, rt];
    set({ gstReturnTypes: next });
  };

  const provider = (data as any).gstProvider || 'taxpro';
  const isWhitebooks = provider === 'whitebooks';
  const draftSecret = (data as any).whitebooksClientSecret ?? '';
  const secretIsSet = (data as any).whitebooksClientSecretSet === true ||
    (typeof (data as any).whitebooksClientSecretEnc === 'string' && (data as any).whitebooksClientSecretEnc.length > 0);

  return (
    <div>
      <SectionTitle>API Provider</SectionTitle>
      <FieldRow label="Active Provider" hint="Which GST Suvidha Provider the app uses for GSTIN lookups and return-status tracking. Switch here if one provider is down.">
        <select className="input-field max-w-md" value={provider}
          onChange={(e) => set({ gstProvider: e.target.value as 'taxpro' | 'whitebooks' })}>
          <option value="taxpro">TaxPro (Chartered Info)</option>
          <option value="whitebooks">Whitebooks (BVM)</option>
        </select>
      </FieldRow>

      {isWhitebooks && (
        <>
          <FieldRow label="Whitebooks Email" hint="The registered user email Whitebooks expects as the `email` query parameter.">
            <input className="input-field max-w-md" value={(data as any).whitebooksEmail || ''}
              onChange={(e) => set({ whitebooksEmail: e.target.value })} placeholder="you@mgsg.in" />
          </FieldRow>
          <FieldRow label="Whitebooks Client ID" hint="The client_id from your Whitebooks → Credentials page.">
            <input className="input-field max-w-md" value={(data as any).whitebooksClientId || ''}
              onChange={(e) => set({ whitebooksClientId: e.target.value })} placeholder="client_id" />
          </FieldRow>
          <FieldRow label="Whitebooks Client Secret"
            hint={secretIsSet
              ? 'A client secret is already stored (encrypted). Leave blank to keep it; enter a new value to replace.'
              : 'Stored encrypted (AES-256-GCM) and never shown again once saved.'}>
            <div className="flex items-center gap-2 max-w-md">
              <input type={showSecret ? 'text' : 'password'} className="input-field flex-1"
                value={draftSecret} onChange={(e) => set({ whitebooksClientSecret: e.target.value })}
                placeholder={secretIsSet ? '•••••••• (leave blank to keep)' : 'Enter client secret'}
                autoComplete="new-password" />
              <button type="button" onClick={() => setShowSecret((s) => !s)}
                className="text-xs text-gray-500 hover:text-gray-800 underline">
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
          </FieldRow>
          <div className="mb-2 rounded-md bg-blue-50 p-3 text-xs text-blue-800 max-w-2xl">
            The client secret is encrypted before storage and never returned to the browser — even admins can't read it back here. Set a dedicated <code className="font-mono">ENCRYPTION_KEY</code> on the server for full key separation from <code className="font-mono">JWT_SECRET</code>.
          </div>
        </>
      )}

      <SectionTitle>Automation</SectionTitle>
      <FieldRow label="Auto-check Filing Status" hint="Background cron checks the GST portal automatically (current default: enabled)">
        <Toggle checked={data.gstAutoCheckEnabled} onChange={(v) => set({ gstAutoCheckEnabled: v })} label={data.gstAutoCheckEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>

      <SectionTitle>Cron Schedule (IST)</SectionTitle>
      <FieldRow label="Morning Check Hour" hint="IST hour for the day-before check (0-23)">
        <NumberInput value={data.gstCheckMorningHour} onChange={(v) => set({ gstCheckMorningHour: v })} min={0} max={23} />
      </FieldRow>
      <FieldRow label="Mid-day Check Hour" hint="IST hour for the day-of mid check (0-23)">
        <NumberInput value={data.gstCheckMidHour} onChange={(v) => set({ gstCheckMidHour: v })} min={0} max={23} />
      </FieldRow>
      <FieldRow label="Evening Check Hour" hint="IST hour for the day-of evening check (0-23)">
        <NumberInput value={data.gstCheckEveningHour} onChange={(v) => set({ gstCheckEveningHour: v })} min={0} max={23} />
      </FieldRow>

      <SectionTitle>API</SectionTitle>
      <FieldRow label="API Rate Limit" hint="Delay between successive GST API calls (ms)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.gstApiRateLimitMs} onChange={(v) => set({ gstApiRateLimitMs: v })} min={0} max={60000} step={50} />
          <span className="text-sm text-gray-500">ms</span>
        </div>
      </FieldRow>

      <SectionTitle>Returns Tracked</SectionTitle>
      <FieldRow label="Return Types" hint="Which GST returns the system tracks and reminds about">
        <div className="flex gap-2 flex-wrap">
          {GST_RETURN_OPTIONS.map((rt) => (
            <button key={rt} type="button" onClick={() => toggleReturn(rt)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                data.gstReturnTypes.includes(rt) ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
              {rt}
            </button>
          ))}
        </div>
      </FieldRow>
    </div>
  );
};

const ReceivablesTab: React.FC<{ data: ReceivablesSettings; onChange: (d: ReceivablesSettings) => void }> = ({ data, onChange }) => {
  const set = (patch: Partial<ReceivablesSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Opening Balance</SectionTitle>
      <FieldRow label="Opening Balance Feature" hint="Enable the opening balance import + tracking for receivables">
        <Toggle checked={data.openingBalanceEnabled} onChange={(v) => set({ openingBalanceEnabled: v })} label={data.openingBalanceEnabled ? 'Enabled' : 'Disabled'} />
      </FieldRow>

      <SectionTitle>Overdue Rules</SectionTitle>
      <FieldRow label="Auto-mark Overdue" hint="Invoices past their due date are automatically marked OVERDUE">
        <Toggle checked={data.autoMarkOverdue} onChange={(v) => set({ autoMarkOverdue: v })} label={data.autoMarkOverdue ? 'Enabled' : 'Disabled'} />
      </FieldRow>
      <FieldRow label="Grace Period" hint="Days past the due date before an invoice is marked OVERDUE (0 = no grace)">
        <div className="flex items-center gap-2">
          <NumberInput value={data.overdueGracePeriodDays} onChange={(v) => set({ overdueGracePeriodDays: v })} min={0} max={365} />
          <span className="text-sm text-gray-500">days</span>
        </div>
      </FieldRow>

      <SectionTitle>Display</SectionTitle>
      <FieldRow label="Group by Billing Entity" hint="Group the receivables list by billing entity by default">
        <Toggle checked={data.groupReceivablesByEntity} onChange={(v) => set({ groupReceivablesByEntity: v })} label={data.groupReceivablesByEntity ? 'Grouped' : 'Flat'} />
      </FieldRow>

      <SectionTitle>Write-offs</SectionTitle>
      <FieldRow label="Max Short & Excess (₹)" hint="Cap on a single Short & Excess write-off — keeps it to genuine rounding/bank-charge residuals. Anything larger must go through Bad Debt instead (Partner/Admin approval).">
        <div className="flex items-center gap-2">
          <NumberInput value={data.maxShortExcessRupees} onChange={(v) => set({ maxShortExcessRupees: v })} min={0} max={100000} />
          <span className="text-sm text-gray-500">₹</span>
        </div>
      </FieldRow>
    </div>
  );
};

// ─── Receipts & Payments ──────────────────────────────────────────────────────

const ReceiptsTab: React.FC<{ data: ReceiptsSettings; onChange: (d: ReceiptsSettings) => void }> = ({ data, onChange }) => {
  const def = ORG_DEFAULTS.receipts;
  const set = (patch: Partial<ReceiptsSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">
        ℹ️ Receipt numbers are one continuous series regardless of source (invoice payment or opening-balance payment). Changing the prefix/padding only affects receipts issued after the change — past receipts keep their printed numbers.
      </p>

      <SectionTitle>Numbering</SectionTitle>
      <FieldRow label="Receipt Number Prefix" hint='e.g. "RCPT" → RCPT/26-27/0001'>
        <div className="flex items-center gap-2 flex-wrap">
          <input className="input-field max-w-[160px] font-mono" value={data.numberPrefix}
            onChange={(e) => set({ numberPrefix: e.target.value.toUpperCase() })} maxLength={12} />
          <DefaultChip value={data.numberPrefix} def={def.numberPrefix} onReset={() => set({ numberPrefix: def.numberPrefix })} />
        </div>
      </FieldRow>
      <FieldRow label="Serial Padding" hint="Number of digits the running serial is zero-padded to">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.numberPadding} onChange={(v) => set({ numberPadding: v })} min={3} max={8} />
          <DefaultChip value={data.numberPadding} def={def.numberPadding} onReset={() => set({ numberPadding: def.numberPadding })} />
        </div>
      </FieldRow>

      <SectionTitle>TDS & Tolerance</SectionTitle>
      <FieldRow label="TDS Auto-Suggest %" hint="When the amount entered on a receipt looks like the outstanding net of this TDS%, the TDS field is pre-filled">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.tdsAutoSuggestPct} onChange={(v) => set({ tdsAutoSuggestPct: v })} min={0} max={50} step={0.5} />
          <span className="text-sm text-gray-500">%</span>
          <DefaultChip value={data.tdsAutoSuggestPct} def={def.tdsAutoSuggestPct} onReset={() => set({ tdsAutoSuggestPct: def.tdsAutoSuggestPct })} />
        </div>
      </FieldRow>
      <FieldRow label="Rounding Tolerance" hint="₹ tolerance used across payment / credit-note / reconciliation 'exceeds outstanding' guards, to absorb paisa rounding">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.roundingToleranceRupees} onChange={(v) => set({ roundingToleranceRupees: v })} min={0} max={100} step={0.5} />
          <span className="text-sm text-gray-500">₹</span>
          <DefaultChip value={data.roundingToleranceRupees} def={def.roundingToleranceRupees} onReset={() => set({ roundingToleranceRupees: def.roundingToleranceRupees })} />
        </div>
      </FieldRow>
      <FieldRow label="PAID Threshold" hint="An invoice is marked PAID once outstanding drops to this amount or below">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.paidThresholdRupees} onChange={(v) => set({ paidThresholdRupees: v })} min={0} max={100} step={0.5} />
          <span className="text-sm text-gray-500">₹</span>
          <DefaultChip value={data.paidThresholdRupees} def={def.paidThresholdRupees} onReset={() => set({ paidThresholdRupees: def.paidThresholdRupees })} />
        </div>
      </FieldRow>

      <SectionTitle>Receipt PDF</SectionTitle>
      <FieldRow label="Disclaimer Text" hint="Printed below the amount-in-words box on the receipt PDF">
        <div className="flex items-start gap-2 flex-wrap">
          <textarea className="input-field max-w-lg" rows={2} value={data.disclaimerText}
            onChange={(e) => set({ disclaimerText: e.target.value })} maxLength={500} />
          <DefaultChip value={data.disclaimerText} def={def.disclaimerText} onReset={() => set({ disclaimerText: def.disclaimerText })} />
        </div>
      </FieldRow>
    </div>
  );
};

// ─── Bank Reconciliation ──────────────────────────────────────────────────────

const ReconciliationTab: React.FC<{ data: ReconciliationSettings; onChange: (d: ReconciliationSettings) => void }> = ({ data, onChange }) => {
  const def = ORG_DEFAULTS.reconciliation;
  const set = (patch: Partial<ReconciliationSettings>) => onChange({ ...data, ...patch });
  const [tdsRatesText, setTdsRatesText] = useState(data.tdsRatesPct.join(', '));
  useEffect(() => { setTdsRatesText(data.tdsRatesPct.join(', ')); }, [data.tdsRatesPct]);
  const commitTdsRates = () => {
    const parsed = tdsRatesText.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n >= 0 && n <= 50);
    if (parsed.length > 0) set({ tdsRatesPct: parsed });
    else setTdsRatesText(data.tdsRatesPct.join(', '));
  };
  return (
    <div>
      <SectionTitle>Amount Matching</SectionTitle>
      <FieldRow label="Exact-Match Tolerance" hint="₹ tolerance for treating a bank credit as an exact-amount match to an invoice">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.amountMatchToleranceRupees} onChange={(v) => set({ amountMatchToleranceRupees: v })} min={0} max={100} step={0.5} />
          <span className="text-sm text-gray-500">₹</span>
          <DefaultChip value={data.amountMatchToleranceRupees} def={def.amountMatchToleranceRupees} onReset={() => set({ amountMatchToleranceRupees: def.amountMatchToleranceRupees })} />
        </div>
      </FieldRow>
      <FieldRow label="TDS-Net Match Tolerance" hint="₹ tolerance when matching a credit that looks like the outstanding net of a guessed TDS rate">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.tdsNetMatchToleranceRupees} onChange={(v) => set({ tdsNetMatchToleranceRupees: v })} min={0} max={100} step={0.5} />
          <span className="text-sm text-gray-500">₹</span>
          <DefaultChip value={data.tdsNetMatchToleranceRupees} def={def.tdsNetMatchToleranceRupees} onReset={() => set({ tdsNetMatchToleranceRupees: def.tdsNetMatchToleranceRupees })} />
        </div>
      </FieldRow>
      <FieldRow label="TDS Rates Tried" hint="Comma-separated % rates tried when a bank credit looks like 'invoice outstanding net of TDS' rather than the full amount">
        <div className="flex items-center gap-2 flex-wrap">
          <input className="input-field max-w-xs" value={tdsRatesText}
            onChange={(e) => setTdsRatesText(e.target.value)} onBlur={commitTdsRates}
            placeholder="10, 2, 5, 7.5" />
          <DefaultChip value={data.tdsRatesPct} def={def.tdsRatesPct} onReset={() => { set({ tdsRatesPct: def.tdsRatesPct }); setTdsRatesText(def.tdsRatesPct.join(', ')); }} />
        </div>
      </FieldRow>

      <SectionTitle>Bulk Confirm</SectionTitle>
      <FieldRow label="Minimum Match Score" hint="Bulk-confirm only auto-accepts a suggestion at or above this score (100 = exact amount + reference match)">
        <div className="flex items-center gap-2 flex-wrap">
          <NumberInput value={data.bulkConfirmMinScore} onChange={(v) => set({ bulkConfirmMinScore: v })} min={0} max={100} />
          <DefaultChip value={data.bulkConfirmMinScore} def={def.bulkConfirmMinScore} onReset={() => set({ bulkConfirmMinScore: def.bulkConfirmMinScore })} />
        </div>
      </FieldRow>
    </div>
  );
};

// ─── Documents & Branding ─────────────────────────────────────────────────────

const DocumentsTab: React.FC<{ data: DocumentsSettings; onChange: (d: DocumentsSettings) => void }> = ({ data, onChange }) => {
  const def = ORG_DEFAULTS.documents;
  const set = (patch: Partial<DocumentsSettings>) => onChange({ ...data, ...patch });
  const isHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);
  return (
    <div>
      <SectionTitle>Branding</SectionTitle>
      <FieldRow label="Header Colour" hint="Header bar colour on generated invoice/credit-note/receipt PDFs">
        <div className="flex items-center gap-2 flex-wrap">
          <input type="color" value={isHex(data.pdfHeaderColor) ? data.pdfHeaderColor : '#0c2340'}
            onChange={(e) => set({ pdfHeaderColor: e.target.value })} className="w-10 h-8 rounded border border-gray-300 cursor-pointer" />
          <input className="input-field max-w-[120px] font-mono" value={data.pdfHeaderColor}
            onChange={(e) => set({ pdfHeaderColor: e.target.value })} maxLength={7} />
          <DefaultChip value={data.pdfHeaderColor} def={def.pdfHeaderColor} onReset={() => set({ pdfHeaderColor: def.pdfHeaderColor })} />
        </div>
      </FieldRow>
      <FieldRow label="Accent Colour" hint="Used for RCM / important-note highlights on invoice PDFs">
        <div className="flex items-center gap-2 flex-wrap">
          <input type="color" value={isHex(data.pdfAccentColor) ? data.pdfAccentColor : '#8b1a1a'}
            onChange={(e) => set({ pdfAccentColor: e.target.value })} className="w-10 h-8 rounded border border-gray-300 cursor-pointer" />
          <input className="input-field max-w-[120px] font-mono" value={data.pdfAccentColor}
            onChange={(e) => set({ pdfAccentColor: e.target.value })} maxLength={7} />
          <DefaultChip value={data.pdfAccentColor} def={def.pdfAccentColor} onReset={() => set({ pdfAccentColor: def.pdfAccentColor })} />
        </div>
      </FieldRow>

      <SectionTitle>Invoice Footer</SectionTitle>
      <FieldRow label="Footer Text" hint="Small print at the bottom of every generated invoice">
        <div className="flex items-center gap-2 flex-wrap">
          <input className="input-field max-w-md" value={data.invoiceFooterText}
            onChange={(e) => set({ invoiceFooterText: e.target.value })} maxLength={200} />
          <DefaultChip value={data.invoiceFooterText} def={def.invoiceFooterText} onReset={() => set({ invoiceFooterText: def.invoiceFooterText })} />
        </div>
      </FieldRow>
    </div>
  );
};

// ─── Email (SMTP) ─────────────────────────────────────────────────────────────

const EmailTab: React.FC<{
  data: EmailSettings & { smtpPassSet?: boolean };
  onChange: (d: EmailSettings) => void;
}> = ({ data, onChange }) => {
  const firmName = useFirmName();
  const set = (patch: Partial<EmailSettings> & { smtpPass?: string }) =>
    onChange({ ...data, ...patch } as any);
  const [showPass, setShowPass] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Plaintext password lives on the draft itself (data.smtpPass) — when the
  // server returns a fresh snapshot after save it has no smtpPass field, so the
  // draft naturally re-syncs and the input clears. This avoids the previous
  // bug where a stale local state kept re-injecting the password into draft
  // and the dirty-comparison never settled.
  const draftPass = (data as any).smtpPass ?? '';
  const passwordIsSet = (data as any).smtpPassSet === true ||
    (typeof data.smtpPassEncrypted === 'string' && data.smtpPassEncrypted.length > 0);

  const runTest = async () => {
    setTestMsg(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo)) {
      setTestMsg({ ok: false, text: 'Enter a valid recipient email' });
      return;
    }
    setTesting(true);
    try {
      const res = await sendTestEmail(testTo);
      if (res.data?.ok) {
        setTestMsg({ ok: true, text: 'Test email sent — check the inbox (and spam).' });
      } else {
        setTestMsg({ ok: false, text: res.data?.error || 'Send failed' });
      }
    } catch (err: any) {
      setTestMsg({ ok: false, text: err?.response?.data?.error || err?.message || 'Send failed' });
    } finally {
      setTesting(false);
    }
  };

  const provider = ((data as any).provider as 'smtp' | 'graph') || 'smtp';
  const isGraph = provider === 'graph';

  return (
    <div>
      <SectionTitle>Email Provider</SectionTitle>
      <FieldRow label="Provider" hint="Microsoft Graph sends via Office 365 over HTTPS (bypasses Railway's SMTP block). SMTP works with any mail server.">
        <select className="input-field max-w-md" value={provider}
          onChange={(e) => set({ provider: e.target.value as 'smtp' | 'graph' } as any)}>
          <option value="smtp">SMTP (nodemailer)</option>
          <option value="graph">Microsoft Graph (Office 365)</option>
        </select>
      </FieldRow>
      {isGraph && (
        <div className="mt-2 mb-4 rounded-md bg-indigo-50 p-3 text-xs text-indigo-900 max-w-3xl">
          <strong>Microsoft Graph requires 4 env vars on the server</strong> (set in Railway → server → Variables):
          <pre className="mt-1 font-mono text-[11px] leading-relaxed">MS_GRAPH_TENANT_ID
MS_GRAPH_CLIENT_ID
MS_GRAPH_CLIENT_SECRET
MS_GRAPH_SENDER</pre>
          The SMTP fields below are ignored when Graph is selected — only <em>From Name</em> is used (as the display name).
        </div>
      )}

      <SectionTitle>SMTP Server {isGraph && <span className="text-xs font-normal text-gray-400">(ignored — Graph is active)</span>}</SectionTitle>
      <FieldRow label="SMTP Host" hint="e.g. smtp.office365.com for Microsoft 365, smtp-relay.brevo.com for Brevo">
        <input className="input-field max-w-md" value={data.smtpHost} disabled={isGraph}
          onChange={(e) => set({ smtpHost: e.target.value })} placeholder="smtp.office365.com" />
      </FieldRow>
      <FieldRow label="Port" hint="587 for STARTTLS (most common), 465 for SSL/TLS">
        <NumberInput value={data.smtpPort} onChange={(v) => set({ smtpPort: v })} min={1} max={65535} />
      </FieldRow>
      <FieldRow label="Use SSL/TLS" hint="Turn ON for port 465. Leave OFF for port 587 (STARTTLS).">
        <Toggle checked={data.secure} onChange={(v) => set({ secure: v })} label={data.secure ? 'SSL/TLS' : 'STARTTLS'} />
      </FieldRow>

      <SectionTitle>Authentication</SectionTitle>
      <FieldRow label="SMTP Username" hint="For Office 365 this is the full mailbox address (e.g. noreply@mgsg.in)">
        <input className="input-field max-w-md" value={data.smtpUser}
          onChange={(e) => set({ smtpUser: e.target.value })} placeholder="noreply@mgsg.in" />
      </FieldRow>
      <FieldRow label="SMTP Password"
        hint={passwordIsSet
          ? 'A password is already stored (encrypted). Leave blank to keep it; enter a new value to replace.'
          : 'Stored encrypted (AES-256-GCM). For Microsoft 365 mailboxes with MFA, use an App Password.'}>
        <div className="flex items-center gap-2 max-w-md">
          <input type={showPass ? 'text' : 'password'} className="input-field flex-1"
            value={draftPass} onChange={(e) => set({ smtpPass: e.target.value })}
            placeholder={passwordIsSet ? '•••••••• (leave blank to keep)' : 'Enter password'}
            autoComplete="new-password" />
          <button type="button" onClick={() => setShowPass((s) => !s)}
            className="text-xs text-gray-500 hover:text-gray-800 underline">
            {showPass ? 'Hide' : 'Show'}
          </button>
        </div>
      </FieldRow>

      <SectionTitle>Sender Identity</SectionTitle>
      <FieldRow label="From Name" hint="Display name shown to recipients">
        <input className="input-field max-w-md" value={data.fromName}
          onChange={(e) => set({ fromName: e.target.value })} placeholder={`${firmName} Office`} />
      </FieldRow>
      <FieldRow label="From Address" hint="Must match (or have Send-As rights for) the SMTP username — Office 365 rejects mismatches.">
        <input className="input-field max-w-md" value={data.fromAddress}
          onChange={(e) => set({ fromAddress: e.target.value })} placeholder="noreply@mgsg.in" />
      </FieldRow>

      <SectionTitle>Send Test Email</SectionTitle>
      <FieldRow label="Recipient" hint="Save the tab first if you've changed anything, then send a test.">
        <div className="flex items-center gap-2 flex-wrap">
          <input type="email" className="input-field max-w-md" value={testTo}
            onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
          <button type="button" onClick={runTest} disabled={testing}
            className="btn-secondary text-sm whitespace-nowrap">
            {testing ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </FieldRow>
      {testMsg && (
        <div className={`mt-3 rounded-md p-3 text-sm ${testMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {testMsg.ok ? '✓ ' : '✗ '}{testMsg.text}
        </div>
      )}

      <div className="mt-6 rounded-md bg-blue-50 p-3 text-xs text-blue-800">
        <strong>Office 365 tips:</strong>
        <ul className="list-disc ml-5 mt-1 space-y-0.5">
          <li>The mailbox must have a paid Exchange Online license.</li>
          <li>SMTP AUTH must be enabled for the mailbox (Admin → Users → Mail → Manage email apps → Authenticated SMTP).</li>
          <li>If MFA is on, generate an App Password and use that here — not the user's real password.</li>
          <li>From Address must equal SMTP Username (or have explicit Send-As rights).</li>
        </ul>
      </div>
    </div>
  );
};

// ─── Billing Emails (invoice send-to-client templates) ───────────────────────

const BillingTab: React.FC<{
  data: BillingSettings;
  onChange: (d: BillingSettings) => void;
}> = ({ data, onChange }) => {
  const firmName = useFirmName();
  const set = (patch: Partial<BillingSettings>) => onChange({ ...data, ...patch });
  return (
    <div>
      <SectionTitle>Billing Sender</SectionTitle>
      <FieldRow label="Sender Email" hint="Must be a licensed Exchange Online mailbox, added to the Application Access Policy group configured for your Azure app registration.">
        <input className="input-field max-w-md" value={data.senderEmail || ''}
          onChange={(e) => set({ senderEmail: e.target.value })}
          placeholder="accounts@yourfirm.com" />
      </FieldRow>
      <FieldRow label="Sender Name" hint={`Display name shown to clients (e.g. '${firmName} — Accounts').`}>
        <input className="input-field max-w-md" value={data.senderName || ''}
          onChange={(e) => set({ senderName: e.target.value })} />
      </FieldRow>
      <FieldRow label="Default CC" hint="Comma-separated emails added to CC on every invoice send. Use this to BCC yourself / the accounts team.">
        <input className="input-field max-w-md" value={data.defaultCc || ''}
          onChange={(e) => set({ defaultCc: e.target.value })}
          placeholder="accounts@mgsg.in, partner@mgsg.in" />
      </FieldRow>

      <SectionTitle>Email Template</SectionTitle>
      <div className="mb-3 rounded-md bg-indigo-50 p-3 text-xs text-indigo-900 max-w-3xl">
        <strong>Available placeholders</strong> (use inside braces):
        <code className="block mt-1 font-mono text-[11px] leading-5">
          {'{invoiceNumber}  {clientName}  {clientLegalName}  {amount}'}<br />
          {'{invoiceDate}  {dueDate}  {billingEntity}  {firmName}  {senderName}'}
        </code>
        <p className="mt-1.5 text-[11px] text-indigo-700">
          <code className="font-mono">{'{clientLegalName}'}</code> uses the client's recorded legal name (falls back to the regular client name if blank).
        </p>
      </div>
      <FieldRow label="Subject" hint="One-line subject; placeholders allowed.">
        <input className="input-field max-w-2xl w-full" value={data.subjectTemplate || ''}
          onChange={(e) => set({ subjectTemplate: e.target.value })} />
      </FieldRow>
      <FieldRow label="Body (HTML)" hint="Use simple HTML tags (p, strong, br). Placeholders are substituted at send time.">
        <textarea
          className="input-field max-w-2xl w-full font-mono text-xs"
          rows={12}
          value={data.bodyTemplate || ''}
          onChange={(e) => set({ bodyTemplate: e.target.value })}
        />
      </FieldRow>

      <SectionTitle>Attachments</SectionTitle>
      <FieldRow label="Certificate handling" hint="What to do with the certificate file when emailing a certificate invoice.">
        <select
          className="input-field max-w-md"
          value={(data as any).certificateAttachmentMode || 'AUTO'}
          onChange={(e) => set({ certificateAttachmentMode: e.target.value } as any)}
        >
          <option value="AUTO">Auto — attach if it fits, otherwise send as download link</option>
          <option value="ALWAYS_LINK">Always send as a download link</option>
          <option value="NEVER">Never include the certificate file</option>
        </select>
      </FieldRow>
      <FieldRow label="Attachment size limit (MB)" hint="Total attachment size at which we drop oversized cert files to a download link. Microsoft 365 caps a single sendMail call at ~4 MB.">
        <input
          type="number"
          min={1}
          max={25}
          step={1}
          className="input-field max-w-[140px]"
          value={(data as any).attachmentSizeLimitMB ?? 4}
          onChange={(e) => set({ attachmentSizeLimitMB: Number(e.target.value) } as any)}
        />
      </FieldRow>

      <div className="mt-6 rounded-md bg-amber-50 p-3 text-xs text-amber-900 max-w-3xl">
        <strong>Setup reminder:</strong>
        <ul className="list-disc ml-5 mt-1 space-y-0.5">
          <li>The sender mailbox above must be licensed in Microsoft 365.</li>
          <li>It must be covered by an Application Access Policy for your Azure app registration — typically by adding it to the mail-enabled security group named in that policy.</li>
          <li>Run <code className="font-mono">Test-ApplicationAccessPolicy</code> in Exchange PowerShell to verify; expected result is <code>Granted</code>.</li>
        </ul>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

type GroupId = 'firm' | 'people' | 'work' | 'billing' | 'comms';

const GROUPS: Array<{ id: GroupId; label: string }> = [
  { id: 'firm',    label: '🏢 Firm Setup' },
  { id: 'people',  label: '👥 People & Time' },
  { id: 'work',    label: '📋 Work' },
  { id: 'billing', label: '💰 Billing & Money' },
  { id: 'comms',   label: '📨 Communications' },
];

const TABS = [
  // Firm Setup
  { id: 'general',       label: '⚙️ General',         priority: 'P0', group: 'firm'    as GroupId },
  { id: 'security',      label: '🔒 Security',        priority: 'P2', group: 'firm'    as GroupId },
  { id: 'roles',         label: '🛡️ Roles & Access',  priority: 'P2', group: 'firm'    as GroupId },
  { id: 'hr',            label: '👔 HR',              priority: 'P2', group: 'firm'    as GroupId },
  { id: 'storage',       label: '📦 File & Storage',  priority: 'P2', group: 'firm'    as GroupId },
  // People & Time
  { id: 'attendance',    label: '📅 Attendance',      priority: 'P0', group: 'people'  as GroupId },
  { id: 'leave',         label: '🏖️ Leave',           priority: 'P0', group: 'people'  as GroupId },
  { id: 'timesheets',    label: '⏱️ Timesheets',      priority: 'P1', group: 'people'  as GroupId },
  { id: 'reimbursement', label: '💸 Reimbursement',   priority: 'P1', group: 'people'  as GroupId },
  { id: 'kpi',           label: '📊 KPI Thresholds',  priority: 'P1', group: 'people'  as GroupId },
  // Work
  { id: 'tasks',         label: '📋 Tasks',           priority: 'P1', group: 'work'    as GroupId },
  { id: 'tickets',       label: '🎫 IT & Tickets',    priority: 'P1', group: 'work'    as GroupId },
  // Billing & Money
  { id: 'invoicing',     label: '🧾 Invoicing',       priority: 'P1', group: 'billing' as GroupId },
  { id: 'gst',           label: '🇮🇳 GST',             priority: 'P1', group: 'billing' as GroupId },
  { id: 'receivables',   label: '💰 Receivables',     priority: 'P1', group: 'billing' as GroupId },
  { id: 'receipts',      label: '🧾 Receipts & Payments', priority: 'P1', group: 'billing' as GroupId },
  { id: 'reconciliation', label: '🏦 Reconciliation', priority: 'P2', group: 'billing' as GroupId },
  { id: 'documents',     label: '🎨 Documents & Branding', priority: 'P2', group: 'billing' as GroupId },
  { id: 'billing',       label: '🧮 Billing Emails',  priority: 'P1', group: 'billing' as GroupId },
  // Communications
  { id: 'email',         label: '📧 Email (SMTP)',    priority: 'P1', group: 'comms'   as GroupId },
  { id: 'notifications', label: '🔔 Notifications',   priority: 'P1', group: 'comms'   as GroupId },
] as const;

type TabId = typeof TABS[number]['id'];

// Extra searchable keywords per tab, beyond its label — lets the settings
// search box find a tab by a setting name it contains, not just the tab title.
const TAB_KEYWORDS: Partial<Record<TabId, string>> = {
  invoicing: 'tax cgst sgst igst due days hsn sac tds payment mode credit note prefix edit window retainer wizard ope template description',
  receipts: 'receipt number prefix padding tds auto-suggest tolerance paid threshold disclaimer',
  reconciliation: 'bank match tolerance tds rates bulk confirm score',
  documents: 'branding pdf colour color header accent footer logo',
  roles: 'permission partner admin hr staff cancel invoice mark unpaid delete opening balance',
  receivables: 'ageing overdue grace opening balance',
  gst: 'gstin whitebooks taxpro filing return',
  tasks: 'task id prefix archive category',
  general: 'organisation name currency date format timezone fiscal year working days',
  security: 'password session timeout bcrypt',
  storage: 'file size upload vault retention export',
  hr: 'working hours weekly off',
  attendance: 'photo location gps bypass',
  leave: 'quota comp off approval',
  timesheets: 'week lock backdated hours step',
  reimbursement: 'claim receipt auto approve',
  tickets: 'sla priority ticket cost approval',
  notifications: 'reminder cron email sms',
  kpi: 'health score realization collection',
  email: 'smtp host port from address',
  billing: 'sender email subject body template attachment',
};

function matchesSearch(tab: (typeof TABS)[number], q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return tab.label.toLowerCase().includes(needle) || (TAB_KEYWORDS[tab.id] ?? '').includes(needle);
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'never';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return formatDateDdMmmYy(dateStr);
}

const OrgSettings: React.FC = () => {
  const { isAdmin } = useAuth();
  const { settings: serverSettings, refresh, setSettings: cacheSettings } = useOrgSettings();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [tabSearch, setTabSearch] = useState('');
  const visibleTabs = useMemo(() => TABS.filter((t) => matchesSearch(t, tabSearch)), [tabSearch]);
  const visibleGroups = useMemo(() => GROUPS.filter((g) => visibleTabs.some((t) => t.group === g.id)), [visibleTabs]);
  const [draft, setDraft] = useState<OrgSettingsType>(serverSettings);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<TabId | 'all' | null>(null);

  // Auto-select tab from ?tab= URL param (e.g. /org-settings?tab=invoicing)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && TABS.some(t => t.id === tab)) setActiveTab(tab as TabId);
  }, []);

  // Keep draft synced with server state — but only adopt server snapshots that aren't stale-overwriting unsaved edits
  useEffect(() => { setDraft(serverSettings); }, [serverSettings]);

  // ── Per-tab dirty detection ────────────────────────────────────────────────
  const tabDirty: Record<TabId, boolean> = useMemo(() => {
    const out = {} as Record<TabId, boolean>;
    for (const tab of TABS) {
      out[tab.id] = JSON.stringify(draft[tab.id]) !== JSON.stringify(serverSettings[tab.id]);
    }
    return out;
  }, [draft, serverSettings]);

  const anyDirty = useMemo(() => Object.values(tabDirty).some(Boolean), [tabDirty]);
  const dirtyTabIds = useMemo(() => TABS.filter((t) => tabDirty[t.id]).map((t) => t.id), [tabDirty]);

  // How many fields in each (saved) tab differ from the factory default —
  // independent of "dirty" (unsaved), this is "customised vs stock" so an
  // admin can see at a glance what's been changed from defaults, even long
  // after saving.
  const tabCustomised: Record<TabId, number> = useMemo(() => {
    const out = {} as Record<TabId, number>;
    for (const tab of TABS) {
      const current = serverSettings[tab.id] as any;
      const base = (ORG_DEFAULTS as any)[tab.id];
      let count = 0;
      for (const key of Object.keys(base ?? {})) {
        if (JSON.stringify(current?.[key]) !== JSON.stringify(base[key])) count++;
      }
      out[tab.id] = count;
    }
    return out;
  }, [serverSettings]);

  // ── Unsaved-changes navigation guard ───────────────────────────────────────
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Standards-compliant browsers ignore custom messages
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  // Click-through warning when navigating via sidebar
  useEffect(() => {
    if (!anyDirty) return;
    const interceptor = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href === window.location.pathname) return;
      if (!window.confirm('You have unsaved changes. Leave without saving?')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', interceptor, true);
    return () => document.removeEventListener('click', interceptor, true);
  }, [anyDirty]);

  // ── Save handlers ──────────────────────────────────────────────────────────
  const sendUpdate = useCallback(async (payload: Record<string, any>) => {
    setSaving(true);
    setError('');
    try {
      const res = await updateOrgSettings(payload);
      cacheSettings(res.data);
      // setDraft is updated by the effect watching serverSettings
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      return true;
    } catch (err: any) {
      const d = err?.response?.data;
      const errMsg = d?.errors?.join(', ')
        ?? (d?.detail ? `${d.message ?? 'Server error'}: ${d.detail}` : null)
        ?? d?.message
        ?? 'Failed to save settings';
      setError(errMsg);
      return false;
    } finally {
      setSaving(false);
    }
  }, [cacheSettings]);

  const saveCurrentTab = useCallback(async () => {
    if (!tabDirty[activeTab]) return;
    await sendUpdate({ [activeTab]: draft[activeTab] });
  }, [activeTab, draft, tabDirty, sendUpdate]);

  const saveAllTabs = useCallback(async () => {
    if (!anyDirty) return;
    const payload: Record<string, any> = {};
    for (const tab of dirtyTabIds) payload[tab] = draft[tab];
    await sendUpdate(payload);
  }, [draft, dirtyTabIds, anyDirty, sendUpdate]);

  const resetCurrentTab = () => {
    setDraft((prev) => ({ ...prev, [activeTab]: serverSettings[activeTab] }));
  };

  const resetAllTabs = () => {
    setDraft(serverSettings);
  };

  const restoreDefaultsForTab = async (tab: TabId | 'all') => {
    setSaving(true);
    setError('');
    try {
      const categories = tab === 'all' ? Object.keys(ORG_DEFAULTS) : [tab];
      const res = await restoreOrgDefaults(categories);
      cacheSettings(res.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      setError('Failed to restore defaults');
    } finally {
      setSaving(false);
      setConfirmRestore(null);
    }
  };

  const setTabData = (tab: TabId, data: any) => {
    setDraft((prev) => ({ ...prev, [tab]: data }));
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-3">🔒</div>
          <p className="font-medium">Admin access required</p>
          <p className="text-sm mt-1">Only administrators can view organisation settings.</p>
        </div>
      </div>
    );
  }

  const meta = serverSettings._meta;

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Organisation Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure firm-wide defaults and policies</p>
        </div>
        <div className="flex items-center gap-3">
          {anyDirty && (
            <button onClick={() => setConfirmRestore('all')} className="text-xs text-gray-500 hover:text-red-600">
              Restore all defaults
            </button>
          )}
          {anyDirty && (
            <button onClick={resetAllTabs} className="btn-secondary text-sm">Reset All</button>
          )}
          <button
            onClick={saveAllTabs}
            disabled={saving || !anyDirty}
            className={`btn-primary text-sm min-w-[110px] ${(!anyDirty && !saving) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {saving ? 'Saving…' : savedFlash ? '✓ Saved' : `Save All${anyDirty ? ` (${dirtyTabIds.length})` : ''}`}
          </button>
        </div>
      </div>

      {/* Meta line */}
      {meta && (
        <p className="text-xs text-gray-400 mb-4">
          Last updated {relativeTime(meta.updatedAt)}{meta.updatedBy ? ` by ${meta.updatedBy.staffName}` : ''}
        </p>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Search across all sections — filters the sidebar to matching tabs */}
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        <input
          value={tabSearch}
          onChange={(e) => setTabSearch(e.target.value)}
          placeholder="Search settings… (e.g. retainer, receipt, tolerance, GSTIN)"
          className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-300 text-sm"
        />
        {tabSearch && (
          <button onClick={() => setTabSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Grouped sidebar layout — sections collapse on mobile to a select dropdown */}
        <div className="flex flex-col md:flex-row">
          {/* Mobile: simple dropdown picker */}
          <div className="md:hidden p-3 border-b border-gray-200 bg-gray-50">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as TabId)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              {visibleGroups.map((g) => (
                <optgroup key={g.id} label={g.label}>
                  {visibleTabs.filter((t) => t.group === g.id).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}{tabDirty[t.id] ? ' •' : ''}{tabCustomised[t.id] ? ` (${tabCustomised[t.id]} customised)` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {tabSearch && visibleTabs.length === 0 && (
              <p className="text-xs text-gray-400 mt-2">No settings match "{tabSearch}"</p>
            )}
          </div>

          {/* Desktop: left sidebar with section headers */}
          <nav className="hidden md:block w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50 py-3 overflow-y-auto max-h-[calc(100vh-220px)]">
            {visibleTabs.length === 0 && tabSearch && (
              <p className="px-4 text-xs text-gray-400">No settings match "{tabSearch}"</p>
            )}
            {visibleGroups.map((g) => {
              const groupTabs = visibleTabs.filter((t) => t.group === g.id);
              const groupHasDirty = groupTabs.some((t) => tabDirty[t.id]);
              return (
                <div key={g.id} className="mb-3 last:mb-0">
                  <div className="px-4 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                    <span>{g.label}</span>
                    {groupHasDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved changes in this section" />}
                  </div>
                  {groupTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-left pl-6 pr-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-1.5 border-l-2 ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-700 bg-white font-medium'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <span className="truncate">{tab.label}</span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {tabCustomised[tab.id] > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600" title={`${tabCustomised[tab.id]} setting(s) changed from factory default`}>
                            {tabCustomised[tab.id]}
                          </span>
                        )}
                        {tabDirty[tab.id] && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved changes" />}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </nav>

          {/* Tab content */}
          <div className="flex-1 p-6 min-w-0">
          {activeTab === 'general'       && <GeneralTab       data={draft.general}       onChange={(d) => setTabData('general', d)} />}
          {activeTab === 'attendance'    && <AttendanceTab    data={draft.attendance}    onChange={(d) => setTabData('attendance', d)} />}
          {activeTab === 'leave'         && <LeaveTab         data={draft.leave}         onChange={(d) => setTabData('leave', d)} />}
          {activeTab === 'tasks'         && <TasksTab         data={draft.tasks}         onChange={(d) => setTabData('tasks', d)} />}
          {activeTab === 'invoicing'     && <InvoicingTab     data={draft.invoicing}     onChange={(d) => setTabData('invoicing', d)} />}
          {activeTab === 'gst'           && <GstTab           data={draft.gst}           onChange={(d) => setTabData('gst', d)} />}
          {activeTab === 'receivables'   && <ReceivablesTab   data={draft.receivables}   onChange={(d) => setTabData('receivables', d)} />}
          {activeTab === 'receipts'      && <ReceiptsTab      data={draft.receipts}      onChange={(d) => setTabData('receipts', d)} />}
          {activeTab === 'reconciliation' && <ReconciliationTab data={draft.reconciliation} onChange={(d) => setTabData('reconciliation', d)} />}
          {activeTab === 'documents'     && <DocumentsTab     data={draft.documents}     onChange={(d) => setTabData('documents', d)} />}
          {activeTab === 'timesheets'    && <TimesheetsTab    data={draft.timesheets}    onChange={(d) => setTabData('timesheets', d)} />}
          {activeTab === 'reimbursement' && <ReimbursementTab data={draft.reimbursement} onChange={(d) => setTabData('reimbursement', d)} />}
          {activeTab === 'tickets'       && <TicketsTab       data={draft.tickets}       onChange={(d) => setTabData('tickets', d)} />}
          {activeTab === 'notifications' && <NotificationsTab data={draft.notifications} onChange={(d) => setTabData('notifications', d)} />}
          {activeTab === 'email'         && <EmailTab         data={draft.email as any} onChange={(d) => setTabData('email', d)} />}
          {activeTab === 'billing'       && <BillingTab       data={(draft as any).billing} onChange={(d) => setTabData('billing' as any, d)} />}
          {activeTab === 'kpi'           && <KpiTab           data={draft.kpi}           onChange={(d) => setTabData('kpi', d)} />}
          {activeTab === 'roles'         && <RolesTab         data={draft.roles}         onChange={(d) => setTabData('roles', d)} />}
          {activeTab === 'storage'       && <StorageTab       data={draft.storage}       onChange={(d) => setTabData('storage', d)} />}
          {activeTab === 'security'      && <SecurityTab      data={draft.security}      onChange={(d) => setTabData('security', d)} />}
          {activeTab === 'hr'            && <HRTab            data={draft.hr}            onChange={(d) => setTabData('hr', d)} />}
          </div>
        </div>

        {/* Per-tab footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-gray-500">
            {tabDirty[activeTab]
              ? <>⚠️ Unsaved changes in <strong>{TABS.find((t) => t.id === activeTab)?.label}</strong></>
              : <>✓ This tab is saved</>}
          </p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setConfirmRestore(activeTab)} className="text-xs text-gray-500 hover:text-red-600 underline">
              Restore tab defaults
            </button>
            {tabDirty[activeTab] && (
              <button onClick={resetCurrentTab} className="btn-secondary text-sm">Reset Tab</button>
            )}
            <button onClick={saveCurrentTab} disabled={saving || !tabDirty[activeTab]}
              className={`btn-primary text-sm min-w-[110px] ${(!tabDirty[activeTab] && !saving) ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {saving ? 'Saving…' : savedFlash ? '✓ Saved' : 'Save This Tab'}
            </button>
          </div>
        </div>
      </div>

      {/* Restore defaults confirmation modal */}
      {confirmRestore && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Restore defaults?</h2>
            <p className="text-sm text-gray-600 mb-4">
              {confirmRestore === 'all'
                ? 'This will reset ALL settings to factory defaults. This action cannot be undone.'
                : <>This will reset the <strong>{TABS.find((t) => t.id === confirmRestore)?.label}</strong> tab to factory defaults.</>}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmRestore(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={() => restoreDefaultsForTab(confirmRestore)}
                className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
                Restore Defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSettings;
